import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

export type ViewerProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
};

const FOCUSABLE_SELECTOR = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** W13: `isConnected` only says the element is still in the DOM — it stays true for one
 *  that has since become `disabled` or hidden, and `.focus()` then silently no-ops,
 *  leaving `document.activeElement` on `<body>`. Focus is then nowhere: a keyboard user
 *  resumes tabbing from the top of the document and a screen reader loses the overlay's
 *  context entirely. Phase 6 makes that reachable — its "New" action can disable the very
 *  control that opened the dialog while the dialog is open.
 *
 *  So: try the remembered trigger, then CHECK the restore actually took, and otherwise
 *  land on the nearest still-focusable control around it — its own group/nav first (the
 *  spine's `<Group role="group">`, i.e. a sibling view button), then the document as a
 *  last resort. Somewhere sane beats nowhere. */
function restoreFocusTo(target: HTMLElement | null): void {
  if (target?.isConnected) {
    target.focus();
    if (document.activeElement === target) return;
  }
  const scope = (target?.isConnected && target.closest('[role="group"],[role="navigation"]')) || document.body;
  const next = Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).find((el) => el !== target);
  next?.focus();
}

/** Full-height overlay for the big views. Same Radix machinery as Dialog, a
 *  different shape: sticky header with a toolbar slot, scrolling body.
 *
 *  Unlike Dialog, Viewer is never opened through a DialogTrigger — the spine's
 *  NavItem buttons that open Rack View / System Slide are plain buttons outside
 *  this tree. Radix's modal DialogContent restores focus on close via its own
 *  `triggerRef`, which only DialogTrigger populates; without one it still calls
 *  `event.preventDefault()` on close (suppressing FocusScope's own generic
 *  restore-to-previous fallback) and then no-ops on a null ref, so focus would
 *  otherwise go nowhere. Viewer captures whatever had focus when it opened and
 *  restores it itself via its own `onCloseAutoFocus`. */
export function Viewer({ open, onClose, title, toolbar, children }: ViewerProps) {
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (open) {
      previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }, [open]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ground/85 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-[var(--radius-2)] border border-hairline/20 bg-surface text-ink shadow-2xl focus:outline-none"
          // W3: Viewer has no description prop and never renders a
          // DialogPrimitive.Description, so it must opt out of Radix's
          // describedby wiring explicitly. Radix 1.1.x tracks a description
          // count and already leaves the attribute off (verified against the
          // installed 1.1.23: its dist contains no console.* at all and
          // WarningProvider is a no-op passthrough), but earlier 1.0.x always
          // pointed aria-describedby at an id nothing rendered and logged
          // "Missing `Description`" on every open. Stating it here makes the
          // opt-out an invariant of this component rather than a property of
          // whichever Radix version happens to be installed.
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            restoreFocusTo(previouslyFocusedRef.current);
          }}
        >
          <header className="flex shrink-0 items-center gap-4 border-b border-hairline/15 px-5 py-3">
            <DialogPrimitive.Title className="font-display text-base font-semibold">{title}</DialogPrimitive.Title>
            <div className="ml-auto flex items-center gap-2">{toolbar}</div>
            <DialogPrimitive.Close aria-label="Close" className="rounded-[var(--radius-2)] p-1 text-ink-muted hover:bg-surface-high hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </header>
          <div className="flex-1 overflow-auto p-5">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
